// src/services/backoffice/calculationParameterService.ts
// שירות API לניהול פרמטרי חישוב

import { API_BASE_URL } from '../../config/api';
import { handleSessionError } from './authService';

// Types for Calculation Parameters
export interface CalculationParameter {
  id: number;
  parameter_name: string;
  parameter_key: string;
  value: number;
  value_type: 'percentage' | 'fixed' | 'formula';
  description?: string;
  is_active: boolean;
  ts: string;
}

export interface CalculationParameterDetails {
  id: number;
  parameter_name: string;
  parameter_key: string;
  value: number;
  value_type: 'percentage' | 'fixed' | 'formula';
  description?: string;
  is_active: boolean;
  ts: string;
}

export interface SaveParameterData {
  id?: number; // 0 for new parameter, >0 for existing
  parameter_name: string;
  parameter_key: string;
  value: number;
  value_type: 'percentage' | 'fixed' | 'formula';
  description?: string;
}

export interface GetParametersResponse {
  parameters: CalculationParameter[];
  total_count: number;
}

export interface ApiResponse<T = any> {
  res: 'OK' | 'NOK';
  msg: string;
  data?: T;
  error_code?: string;
}

// Helper function to get session token
const getSessionToken = (): string | null => {
  return localStorage.getItem('backoffice_session_id');
};

// Helper function to create FormData
const createFormData = (data: Record<string, any>): FormData => {
  const formData = new FormData();

  // הוספת session token (חובה!)
  const sessionToken = getSessionToken();
  if (sessionToken) {
    formData.append('session_token', sessionToken);
  }

  Object.entries(data).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      formData.append(key, value.toString());
    }
  });

  return formData;
};

// פונקציה לטיפול בresponse עם session error
const handleApiResponse = async (response: Response): Promise<any> => {
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const result = await response.json();

  // בדיקת session expiry
  if (handleSessionError(result)) {
    // הפונקציה כבר טיפלה בשגיאה (ניקתה token והפנתה לlogin)
    throw new Error('Session expired - redirecting to login');
  }

  return result;
};

// Get all calculation parameters
export const getCalculationParameters = async (): Promise<GetParametersResponse> => {
  const sessionToken = getSessionToken();
  if (!sessionToken) {
    throw new Error('No session token found');
  }

  // console.log('🔧 [DEBUG] Fetching calculation parameters...');

  const formData = createFormData({});

  const response = await fetch(`${API_BASE_URL}/Api/service/backoffice/get_calculation_parameters`, {
    method: 'POST',
    body: formData
  });

  const result: ApiResponse<GetParametersResponse> = await handleApiResponse(response);

  if (result.res === 'NOK') {
    console.error('🔧 [DEBUG] Get parameters failed:', result.msg);
    throw new Error(result.msg || 'Failed to fetch calculation parameters');
  }

  // console.log('🔧 [DEBUG] Parameters fetched successfully:', result.data);
  return result.data!;
};

// Get calculation parameter details by ID
export const getCalculationParameterDetails = async (parameterId: number): Promise<CalculationParameterDetails> => {
  const sessionToken = getSessionToken();
  if (!sessionToken) {
    throw new Error('No session token found');
  }

  // console.log('🔧 [DEBUG] Fetching parameter details for ID:', parameterId);

  const requestData = {
    parameter_id: parameterId
  };

  const formData = createFormData(requestData);

  const response = await fetch(`${API_BASE_URL}/Api/service/backoffice/get_calculation_parameter_details`, {
    method: 'POST',
    body: formData
  });

  const result: ApiResponse<{ parameter: CalculationParameterDetails }> = await handleApiResponse(response);

  if (result.res === 'NOK') {
    console.error('🔧 [DEBUG] Get parameter details failed:', result.msg);
    throw new Error(result.msg || 'Failed to fetch parameter details');
  }

  // console.log('🔧 [DEBUG] Parameter details fetched successfully:', result.data?.parameter);
  return result.data!.parameter;
};

// Save calculation parameter (create or update)
export const saveCalculationParameter = async (parameterData: SaveParameterData): Promise<void> => {
  const sessionToken = getSessionToken();
  if (!sessionToken) {
    throw new Error('No session token found');
  }

  // ולידציות בסיסיות
  if (!parameterData.parameter_name?.trim()) {
    throw new Error('שם הפרמטר נדרש');
  }

  if (!parameterData.parameter_key?.trim()) {
    throw new Error('מפתח הפרמטר נדרש');
  }

  if (!/^[a-z_]+$/.test(parameterData.parameter_key)) {
    throw new Error('מפתח הפרמטר יכול להכיל רק אותיות אנגליות קטנות וקו תחתון');
  }

  if (isNaN(parameterData.value)) {
    throw new Error('ערך הפרמטר חייב להיות מספר');
  }

  if (!['percentage', 'fixed', 'formula'].includes(parameterData.value_type)) {
    throw new Error('סוג ערך לא תקין');
  }

  const requestData: Record<string, any> = {
    parameter_id: parameterData.id || 0,
    parameter_name: parameterData.parameter_name,
    parameter_key: parameterData.parameter_key,
    value: parameterData.value,
    value_type: parameterData.value_type,
    description: parameterData.description || ''
  };

  // console.log('🔧 [DEBUG] Saving calculation parameter with data:', requestData);

  const formData = createFormData(requestData);

  const response = await fetch(`${API_BASE_URL}/Api/service/backoffice/save_calculation_parameter`, {
    method: 'POST',
    body: formData
  });

  const result: ApiResponse = await handleApiResponse(response);

  if (result.res === 'NOK') {
    console.error('🔧 [DEBUG] Save parameter failed:', result.msg);

    // טיפול בשגיאות ספציפיות
    if (result.error_code === 'PARAMETER_KEY_EXISTS') {
      throw new Error('מפתח הפרמטר כבר קיים במערכת');
    } else if (result.error_code === 'PARAMETER_NOT_FOUND') {
      throw new Error('הפרמטר לא נמצא');
    } else if (result.error_code === 'INVALID_VALUE_TYPE') {
      throw new Error('סוג ערך לא תקין');
    } else if (result.error_code === 'INVALID_VALUE') {
      throw new Error('ערך לא תקין');
    }

    throw new Error(result.msg || 'Failed to save calculation parameter');
  }

  // console.log('🔧 [DEBUG] Parameter saved successfully');
};